// Phase 5Z.25 — Reusable confirm dialog (replaces browser window.confirm)
import { AlertTriangle, X } from "lucide-react";

/**
 * <ConfirmDialog
 *    open={bool}
 *    title="Wirklich löschen?"
 *    message="Diese Aktion kann nicht rückgängig gemacht werden."
 *    confirmLabel="Ja, löschen"
 *    cancelLabel="Abbrechen"
 *    tone="danger"  // "danger" | "warn" | "info"
 *    onConfirm={fn}
 *    onCancel={fn}
 *    busy={bool}
 *    testid="confirm-delete"
 * />
 */
export default function ConfirmDialog({
  open, title = "Bestätigen", message,
  confirmLabel = "Bestätigen", cancelLabel = "Abbrechen",
  tone = "danger", onConfirm, onCancel, busy = false,
  testid = "confirm-dialog",
}) {
  if (!open) return null;

  const tones = {
    danger: { icon: "text-red-400", btn: "bg-red-600 hover:bg-red-500 text-white", border: "border-red-500/40", bg: "bg-red-500/5" },
    warn:   { icon: "text-amber-400", btn: "bg-amber-600 hover:bg-amber-500 text-white", border: "border-amber-500/40", bg: "bg-amber-500/5" },
    info:   { icon: "text-sky-400", btn: "bg-sky-600 hover:bg-sky-500 text-white", border: "border-sky-500/40", bg: "bg-sky-500/5" },
  };
  const T = tones[tone] || tones.danger;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={busy ? undefined : onCancel}
      data-testid={testid}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`bg-zinc-950 border ${T.border} rounded-lg w-full max-w-md shadow-2xl overflow-hidden`}
      >
        <div className={`flex items-start gap-3 p-5 ${T.bg}`}>
          <div className={`shrink-0 ${T.icon}`}>
            <AlertTriangle className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-semibold text-zinc-50 text-base">{title}</h3>
            {message && (
              <p className="mt-1 text-sm text-zinc-300 whitespace-pre-line">{message}</p>
            )}
          </div>
          <button
            onClick={onCancel}
            disabled={busy}
            className="shrink-0 -mt-1 -mr-1 p-1 rounded text-zinc-500 hover:text-zinc-300 disabled:opacity-50"
            aria-label="Schließen"
            data-testid={`${testid}-close`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 bg-zinc-900/50 border-t border-zinc-800">
          <button
            onClick={onCancel}
            disabled={busy}
            data-testid={`${testid}-cancel`}
            className="px-4 py-2 text-sm rounded-md border border-zinc-700 text-zinc-200 hover:bg-zinc-800 disabled:opacity-50 transition-colors"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            data-testid={`${testid}-confirm`}
            className={`px-4 py-2 text-sm rounded-md font-medium ${T.btn} disabled:opacity-50 transition-colors`}
          >
            {busy ? "..." : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
